import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import DataTableSkeleton from "../../components/DataTableSkeleton";
import {
  formatCurrency,
  getAdminAffiliatePayoutProfile,
  getAdminCommissions,
  normalizeCommissionList,
  recordAdminPayout,
} from "../../helper/affiliate_helper";
import { dataTableCustomStyles } from "../../components/admin/dataTableConfig";
import DataTable from "react-data-table-component";

const copyText = async (value, label) => {
  if (!value) {
    toast.warning(`No ${label} to copy`);
    return;
  }
  try {
    await navigator.clipboard.writeText(String(value));
    toast.success(`${label} copied`);
  } catch {
    toast.error("Copy failed");
  }
};

const CopyableField = ({ value, label }) => {
  const text = value === null || value === undefined ? "" : String(value).trim();
  if (!text) return "—";

  return (
    <div className="d-flex align-items-center justify-content-between gap-2">
      <span className="text-break">{text}</span>
      <Button
        type="button"
        size="sm"
        variant="outline-secondary"
        className="py-0 px-1 flex-shrink-0"
        title={`Copy ${label}`}
        onClick={() => copyText(text, label)}
      >
        <i className="ri-file-copy-line" />
      </Button>
    </div>
  );
};

export default function RecordAffiliatePayout() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const affiliateId = (searchParams.get("affiliateId") || "").trim();
  const initialCommissionIds = (searchParams.get("commissionIds") || "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);

  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const [commissions, setCommissions] = useState([]);
  const [commissionsLoading, setCommissionsLoading] = useState(false);
  const [selectedCommissionIds, setSelectedCommissionIds] = useState(initialCommissionIds);

  const [payoutMethod, setPayoutMethod] = useState("upi");
  const [transactionReference, setTransactionReference] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadProfile = async (affiliateId) => {
    if (!affiliateId.trim()) {
      toast.warning("Enter an affiliate ID");
      return;
    }

    setProfileLoading(true);
    setProfile(null);
    try {
      const res = await getAdminAffiliatePayoutProfile(affiliateId.trim());
      if (res?.success === false) {
        toast.error(res?.message || "Failed to load payout profile");
        return;
      }
      const nextProfile = res?.data?.profile || res?.data;
      setProfile(nextProfile);
      if (nextProfile?.preferredPayoutMethod) {
        setPayoutMethod(nextProfile.preferredPayoutMethod);
      }
    } catch (error) {
      if (error?.status !== 401) {
        toast.error(error?.message || "Failed to load payout profile");
      }
    } finally {
      setProfileLoading(false);
    }
  };

  const loadApprovedCommissions = async (affiliateId) => {
    if (!affiliateId.trim()) return;

    setCommissionsLoading(true);
    try {
      const res = await getAdminCommissions({
        affiliateId: affiliateId.trim(),
        status: "approved",
        limit: 100,
        page: 1,
      });
      const list = normalizeCommissionList(res);
      if (res?.success === false && !list.length) {
        toast.error(res?.message || "Failed to load approved commissions");
        setCommissions([]);
        return;
      }
      setCommissions(list);
      if (initialCommissionIds.length) {
        const validIds = initialCommissionIds.filter((id) =>
          list.some((row) => (row.id || row._id) === id)
        );
        setSelectedCommissionIds(validIds);
      }
    } catch (error) {
      if (error?.status !== 401) {
        toast.error(error?.message || "Failed to load approved commissions");
      }
      setCommissions([]);
    } finally {
      setCommissionsLoading(false);
    }
  };

  useEffect(() => {
    if (affiliateId) {
      loadProfile(affiliateId);
      loadApprovedCommissions(affiliateId);
    } else {
      setProfile(null);
      setCommissions([]);
      setSelectedCommissionIds([]);
    }
  }, [affiliateId]);

  const handleLoadAffiliate = () => {
    if (!affiliateId) {
      toast.warning("Affiliate ID is required. Open this page from Affiliate Users.");
      return;
    }
    setSelectedCommissionIds([]);
    loadProfile(affiliateId);
    loadApprovedCommissions(affiliateId);
  };

  const selectedCommissions = useMemo(
    () => commissions.filter((row) => selectedCommissionIds.includes(row.id || row._id)),
    [commissions, selectedCommissionIds]
  );

  const runningTotal = useMemo(
    () => selectedCommissions.reduce((sum, row) => sum + Number(row.commissionAmount || 0), 0),
    [selectedCommissions]
  );

  const bankDetails = profile?.bankDetails || {};
  const canSubmitUpi = Boolean(profile?.upiId?.trim());
  const canSubmitBank = Boolean(
    bankDetails.accountNumber?.trim() &&
    bankDetails.ifscCode?.trim() &&
    bankDetails.accountHolderName?.trim()
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!affiliateId) {
      toast.warning("Affiliate ID is required");
      return;
    }
    if (!selectedCommissionIds.length) {
      toast.warning("Select at least one approved commission");
      return;
    }
    if (!transactionReference.trim()) {
      toast.warning("Transaction reference / UTR is required");
      return;
    }
    if (payoutMethod === "upi" && !canSubmitUpi) {
      toast.error("Affiliate UPI ID is not configured");
      return;
    }
    if (payoutMethod === "bank_transfer" && !canSubmitBank) {
      toast.error("Affiliate bank details are incomplete");
      return;
    }

    setSubmitting(true);
    try {
      const res = await recordAdminPayout({
        affiliateId,
        commissionIds: selectedCommissionIds,
        payoutMethod,
        transactionReference: transactionReference.trim(),
        notes: notes.trim() || undefined,
      });
      if (res?.success === false) {
        toast.error(res?.message || "Failed to record payout");
        return;
      }
      toast.success(res?.message || "Payout recorded successfully");
      navigate("/affiliate-payouts");
    } catch (error) {
      if (error?.status !== 401) {
        toast.error(error?.message || "Failed to record payout");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const commissionColumns = [
    {
      name: "Order",
      selector: (row) => row.orderNumber || "—",
      minWidth: "140px",
    },
    {
      name: "Amount",
      selector: (row) => row.commissionAmount ?? 0,
      minWidth: "110px",
      cell: (row) => formatCurrency(row.commissionAmount, row.currency),
    },
    {
      name: "Date",
      selector: (row) => row.createdAt || "",
      minWidth: "120px",
    },
  ];

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-md-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="/admin/dashboard">Dashboard</Link></li>
              <li className="breadcrumb-item"><Link to="/affiliate-payouts">Payouts</Link></li>
              <li className="breadcrumb-item active" aria-current="page">Record payout</li>
            </ol>
            <h4 className="main-title mb-0 admin-page-title">Record Manual Payout</h4>
            <p className="text-secondary small mb-0 mt-1">
              Pay the affiliate externally, then record the UTR here
            </p>
          </div>
        </div>

        <Card className="border-0 shadow-sm mb-3">
          <Card.Body>
            <Row className="g-2 align-items-end">
              <Col md={6}>
                <Form.Label className="small text-muted mb-1">Affiliate ID</Form.Label>
                <Form.Control
                  value={affiliateId}
                  readOnly
                  plaintext={false}
                  className="bg-light"
                  placeholder="Open from Affiliate Users to load an affiliate"
                />
              </Col>
              <Col md={3}>
                <Button
                  variant="primary"
                  className="w-100"
                  onClick={handleLoadAffiliate}
                  disabled={profileLoading || !affiliateId}
                >
                  Load profile
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {profileLoading ? (
          <DataTableSkeleton rows={4} columns={4} />
        ) : profile ? (
          <>
            <Card className="border-0 shadow-sm mb-3">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h5 className="mb-1">{profile.name || "Affiliate"}</h5>
                    <div className="text-muted small">{profile.email}</div>
                    <div className="text-muted small">Referral: {profile.referralCode || "—"}</div>
                  </div>
                  {/* <Badge bg={profile.status === "active" ? "success" : "secondary"}>
                    {profile.status || "unknown"}
                  </Badge> */}
                </div>

                <Row className="g-3 mb-3">
                  <Col md={4}>
                    <div className="border rounded p-3 h-100">
                      <small className="text-muted d-block">Payable (approved)</small>
                      <strong>{formatCurrency(profile.payableBalance, profile.currency)}</strong>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="border rounded p-3 h-100">
                      <small className="text-muted d-block">Pending</small>
                      <strong>{formatCurrency(profile.pendingBalance, profile.currency)}</strong>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="border rounded p-3 h-100">
                      <small className="text-muted d-block">Paid</small>
                      <strong>{formatCurrency(profile.paidBalance, profile.currency)}</strong>
                    </div>
                  </Col>
                </Row>

                <Row className="g-3">
                  <Col md={6}>
                    <h6 className="mb-2">UPI</h6>
                    <div className="d-flex align-items-center gap-2">
                      <code>{profile.upiId || "Not set"}</code>
                      {profile.upiId && (
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          className="py-0 px-1"
                          title="Copy UPI ID"
                          onClick={() => copyText(profile.upiId, "UPI ID")}
                        >
                          <i className="ri-file-copy-line" />
                        </Button>
                      )}
                    </div>
                  </Col>
                  <Col md={6}>
                    <h6 className="mb-2">Bank details</h6>
                    <Table bordered size="sm" className="mb-0">
                      <tbody>
                        <tr>
                          <th>Holder</th>
                          <td>
                            <CopyableField value={bankDetails.accountHolderName} label="Account holder name" />
                          </td>
                        </tr>
                        <tr>
                          <th>Account</th>
                          <td>
                            <CopyableField value={bankDetails.accountNumber} label="Account number" />
                          </td>
                        </tr>
                        <tr>
                          <th>IFSC</th>
                          <td>
                            <CopyableField value={bankDetails.ifscCode} label="IFSC code" />
                          </td>
                        </tr>
                        <tr>
                          <th>Bank</th>
                          <td>
                            <CopyableField value={bankDetails.bankName} label="Bank name" />
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-3">
              <Card.Body>
                <h5 className="mb-3">Select approved commissions</h5>
                {commissionsLoading ? (
                  <DataTableSkeleton rows={4} columns={3} />
                ) : commissions.length === 0 ? (
                  <p className="text-muted mb-0">No approved commissions for this affiliate.</p>
                ) : (
                  <DataTable
                    columns={commissionColumns}
                    data={commissions}
                    customStyles={dataTableCustomStyles}
                    selectableRows
                    selectableRowsHighlight
                    onSelectedRowsChange={({ selectedRows }) =>
                      setSelectedCommissionIds(selectedRows.map((row) => row.id || row._id).filter(Boolean))
                    }
                    selectableRowSelected={(row) =>
                      selectedCommissionIds.includes(row.id || row._id)
                    }
                  />
                )}
                <div className="mt-3 fw-medium">
                  Selected total: {formatCurrency(runningTotal, profile.currency || "INR")}
                </div>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm">
              <Card.Body>
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Payment method</Form.Label>
                    <div>
                      <Form.Check
                        inline
                        type="radio"
                        id="payout-upi"
                        label="UPI"
                        name="payoutMethod"
                        value="upi"
                        checked={payoutMethod === "upi"}
                        onChange={() => setPayoutMethod("upi")}
                      />
                      <Form.Check
                        inline
                        type="radio"
                        id="payout-bank"
                        label="Bank transfer"
                        name="payoutMethod"
                        value="bank_transfer"
                        checked={payoutMethod === "bank_transfer"}
                        onChange={() => setPayoutMethod("bank_transfer")}
                      />
                    </div>
                    {payoutMethod === "upi" && !canSubmitUpi && (
                      <div className="text-danger small mt-1">Affiliate UPI ID is required for UPI payout.</div>
                    )}
                    {payoutMethod === "bank_transfer" && !canSubmitBank && (
                      <div className="text-danger small mt-1">Complete bank account, IFSC, and holder name are required.</div>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Transaction reference / UTR</Form.Label>
                    <Form.Control
                      value={transactionReference}
                      onChange={(e) => setTransactionReference(e.target.value)}
                      placeholder="UTR or bank reference number"
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label>Notes (optional)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Internal note for this payout"
                    />
                  </Form.Group>

                  <div className="d-flex gap-2">
                    <Button type="submit" variant="primary" disabled={submitting}>
                      {submitting ? "Recording…" : "Record payout"}
                    </Button>
                    <Link to="/affiliate-payouts" className="btn btn-outline-secondary">Cancel</Link>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </>
        ) : (
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-muted">
              Enter an affiliate ID and load their payout profile to continue.
            </Card.Body>
          </Card>
        )}

        <Footer />
      </div>
    </React.Fragment>
  );
}
